import React, { Fragment, useState } from "react";
import { NavLink } from "react-router-dom";
import { Box, ButtonBase, Icon, styled, Tooltip } from "@mui/material";
import useSettings from "app/hooks/useSettings";
import { Paragraph, Span } from "../Typography";
//import MatxVerticalNavExpansionPanel from "./MatxVerticalNavExpansionPanel";
import clsx from "clsx";
import MatxVerticalNavExpansionPanel, {
  BaseButton,
  ItemText,
  BulletIcon as ParentBulletIcon,
  BadgeValue as ParentBadgeValue,
} from "./MatxVerticalNavExpansionPanel";

// ── CSS injection ─────────────────────────────────────────────────────────────
if (
  typeof document !== "undefined" &&
  !document.getElementById("__nav_styles__")
) {
  const s = document.createElement("style");
  s.id = "__nav_styles__";
  s.textContent = `
    @keyframes nav-slide-in { from { opacity:0; transform:translateX(-6px) } to { opacity:1; transform:translateX(0) } }
    @keyframes nav-glow     { 0%,100%{ opacity:.6 } 50%{ opacity:1 } }
    @keyframes nav-pulse    { 0%,100%{ transform:scale(1) } 50%{ transform:scale(1.15) } }
  `;
  document.head.appendChild(s);
}

// ── Label ─────────────────────────────────────────────────────────────────────
const ListLabel = styled(Paragraph)(({ theme, mode }) => ({
  fontSize: "9.5px",
  marginTop: "22px",
  marginLeft: "18px",
  marginBottom: "6px",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  display: mode === "compact" ? "none" : "block",
  color: "rgba(255,255,255,0.35)",
  fontWeight: 700,
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -3,
    left: 0,
    width: 20,
    height: 1,
    background: "rgba(255,255,255,0.15)",
    borderRadius: 2,
  },
}));

// ── Shared nav item styles ─────────────────────────────────────────────────────
const NavItemBase = {
  display: "flex",
  alignItems: "center",
  overflow: "hidden",
  height: 40,
  whiteSpace: "pre",
  marginBottom: "3px",
  marginLeft: "8px",
  marginRight: "8px",
  textDecoration: "none",
  justifyContent: "flex-start",
  paddingLeft: "10px",
  borderRadius: "10px",
  transition: "all 0.18s cubic-bezier(.4,0,.2,1)",
  position: "relative",
  cursor: "pointer",

  // default icon/text
  "& .icon": {
    fontSize: "19px",
    color: "rgba(255,255,255,0.55)",
    transition: "all 0.18s ease",
  },
  "& .nav-text": {
    color: "rgba(255,255,255,0.7)",
    transition: "color 0.18s ease",
  },
  "& .nav-bullet": {
    background: "rgba(255,255,255,0.3)",
    transition: "all 0.18s ease",
  },
  "& .nav-badge": { transition: "all 0.18s ease" },

  // hover
  "&:hover": {
    background: "rgba(255,255,255,0.08)",
    "& .icon": { color: "#fff", transform: "scale(1.08)" },
    "& .nav-text": { color: "#fff" },
    "& .nav-bullet": { background: "#60a5fa", transform: "scale(1.3)" },
  },

  // active
  "&.navItemActive": {
    background: "rgba(255,255,255,0.13)",
    boxShadow: "0 2px 12px rgba(0,0,0,0.2)",
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: "50%",
      transform: "translateY(-50%)",
      width: 3,
      height: "60%",
      borderRadius: "0 3px 3px 0",
      background: "linear-gradient(180deg,#60a5fa,#3b82f6)",
      animation: "nav-glow 2s ease infinite",
    },
    "& .icon": { color: "#93c5fd" },
    "& .nav-text": { color: "#fff", fontWeight: 600 },
    "& .nav-bullet": {
      background: "#60a5fa",
      animation: "nav-pulse 2s ease infinite",
    },
  },

  "&.compactNavItem": {
    justifyContent: "center !important",
    marginLeft: 6,
    marginRight: 6,
  },
};

const ExternalLink = styled("a")(() => ({ ...NavItemBase }));
const InternalLink = styled(Box)(() => ({
  "& a": { ...NavItemBase },
  "& .navItemActive": { background: "rgba(255,255,255,0.13)" },
}));

const StyledText = styled(Span)(({ mode }) => ({
  fontSize: "0.82rem",
  paddingLeft: "0.5rem",
  color: "rgba(255,255,255,0.75)",
  display: mode === "compact" ? "none" : "block",
  fontWeight: 500,
  letterSpacing: "0.01em",
}));

const BulletIcon = styled("div")(() => ({
  width: 7,
  height: 7,
  borderRadius: "50%",
  marginLeft: 22,
  marginRight: 10,
  flexShrink: 0,
  background: "rgba(255,255,255,0.3)",
  transition: "all 0.18s ease",
}));

const BadgeValue = styled("div")(({ color = "#3b82f6" }) => ({
  padding: "1px 7px",
  borderRadius: "300px",
  fontSize: "10px",
  fontWeight: 700,
  background: `${color}33`,
  color: color,
  border: `1px solid ${color}55`,
  marginRight: 4,
  minWidth: 20,
  textAlign: "center",
  animation: "nav-pulse 3s ease infinite",
}));

// ── Main component ────────────────────────────────────────────────────────────
export default function MatxVerticalNav({ items }) {
  const { settings } = useSettings();
  const { mode } = settings.layout1Settings.leftSidebar;
  const [expandedParent, setExpandedParent] = useState(null);

  const renderLevels = (data) =>
    data.map((item, index) => {
      if (item.looksLikeParent) {
        return (
          <Tooltip
            key={index}
            title={mode === "compact" ? item.name : ""}
            placement="right"
            arrow
          >
            <NavLink
              to={item.path}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <BaseButton
                className={mode === "compact" ? "compactNavItem" : ""}
              >
                <Box display="flex" alignItems="center">
                  {item.icon && <Icon className="icon">{item.icon}</Icon>}
                  {item.iconText && <ParentBulletIcon />}
                  <ItemText className="sidenavHoverShow">{item.name}</ItemText>
                </Box>
                {item.badge && (
                  <ParentBadgeValue className="sidenavHoverShow itemIcon">
                    {item.badge.value}
                  </ParentBadgeValue>
                )}
                <Box sx={{ width: 20, flexShrink: 0 }} />
              </BaseButton>
            </NavLink>
          </Tooltip>
        );
      }
      if (item.type === "label") {
        return (
          <ListLabel key={index} mode={mode} className="sidenavHoverShow">
            {item.label}
          </ListLabel>
        );
      }

      if (item.children) {
        return (
          <MatxVerticalNavExpansionPanel
            key={index}
            item={item}
            mode={mode}
            expandedParent={expandedParent}
            setExpandedParent={setExpandedParent}
          >
            {renderLevels(item.children)}
          </MatxVerticalNavExpansionPanel>
        );
      }

      if (item.type === "extLink") {
        return (
          <Tooltip
            key={index}
            title={mode === "compact" ? item.name : ""}
            placement="right"
            arrow
          >
            <ExternalLink
              href={item.path}
              className={mode === "compact" ? "compactNavItem" : ""}
              rel="noopener noreferrer"
              target="_blank"
            >
              <ButtonBase sx={{ width: "100%", borderRadius: "10px" }}>
                {item.icon ? (
                  <Icon className="icon">{item.icon}</Icon>
                ) : (
                  <span className="item-icon icon-text">{item.iconText}</span>
                )}
                <StyledText mode={mode} className="nav-text sidenavHoverShow">
                  {item.name}
                </StyledText>
                <Box mx="auto" />
                {item.badge && (
                  <BadgeValue className="sidenavHoverShow">
                    {item.badge.value}
                  </BadgeValue>
                )}
              </ButtonBase>
            </ExternalLink>
          </Tooltip>
        );
      }

      // Internal link
      return (
        <Tooltip
          key={index}
          title={mode === "compact" ? item.name : ""}
          placement="right"
          arrow
        >
          <InternalLink>
            <NavLink
              to={item.path}
              className={({ isActive }) =>
                [
                  isActive ? "navItemActive" : "",
                  mode === "compact" ? "compactNavItem" : "",
                ]
                  .filter(Boolean)
                  .join(" ")
              }
            >
              <ButtonBase sx={{ width: "100%", borderRadius: "10px" }}>
                {item?.icon ? (
                  <Icon className="icon" sx={{ width: 36, flexShrink: 0 }}>
                    {item.icon}
                  </Icon>
                ) : (
                  <Fragment>
                    <BulletIcon
                      className="nav-bullet"
                      sx={{ display: mode === "compact" ? "none" : "block" }}
                    />
                    <Box
                      className="nav-bullet-text"
                      sx={{
                        ml: "20px",
                        fontSize: "11px",
                        color: "rgba(255,255,255,0.5)",
                        display: mode !== "compact" ? "none" : "block",
                      }}
                    >
                      {item.iconText}
                    </Box>
                  </Fragment>
                )}
                <StyledText mode={mode} className="nav-text sidenavHoverShow">
                  {item.name}
                </StyledText>
                <Box mx="auto" />
                {item.badge && (
                  <BadgeValue
                    color={item.badge.color || "#3b82f6"}
                    className="sidenavHoverShow"
                  >
                    {item.badge.value}
                  </BadgeValue>
                )}
              </ButtonBase>
            </NavLink>
          </InternalLink>
        </Tooltip>
      );
    });

  return (
    <Box
      className="navigation"
      sx={{
        height: "100%",
        minHeight: "calc(100vh - 64px)",
        paddingTop: "8px",
        paddingBottom: "16px",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        // ── Scrollbar ──
        overflowY: "auto",
        overflowX: "hidden",
        "&::-webkit-scrollbar": { width: 4 },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": {
          background: "rgba(255,255,255,0.15)",
          borderRadius: 4,
          "&:hover": { background: "rgba(255,255,255,0.28)" },
        },
        // ── Gradient background ──
        background:
          "linear-gradient(180deg, #0d4f85 0%, #0a3d6b 60%, #082f54 100%)",
        // ── Top separator ──
        borderTop: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      {renderLevels(items)}

      {/* Bottom fade */}
      <Box sx={{ flexGrow: 1 }} />
      <Box
        sx={{
          height: 40,
          background: "linear-gradient(to top, rgba(8,47,84,0.9), transparent)",
          pointerEvents: "none",
          flexShrink: 0,
        }}
      />
    </Box>
  );
}
